import React, { useState, useRef, useCallback } from 'react';
import { PenTool, Search, Loader2, CheckCircle, Package, AlertCircle, Camera } from 'lucide-react';
import { pickingService } from '../../../../services/pickingService';
import { mediaAttachmentService } from '../../../../services/mediaAttachmentService';
import { compressImage } from '../../../common/media/MediaCaptureButton';
import { HandoverPListDto } from '../../../../types/picking';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';
import { StatusBadge } from '../../../common/StatusBadge';
import { ScanTextInput } from '../../../common/inputs';
import { SignatureModal } from '../picking/modals/SignatureModal';

interface DeliveryHandoverProps {
  onBack: () => void;
}

/**
 * Utlevering (Teamwork 36334240): kunde/sjåfør henter varer på lageret og signerer.
 *
 * Scope (vedtatt): KUN signatur — registreres på nøyaktig samme måte som når en
 * kunde signerer på en plukkliste i plukkbildet (SignatureModal → Signature1 på
 * PList via updatePickingList). Ingen datamodell-endring. Oppslag på ORDRENIVÅ:
 * én ordre kan omfatte flere plukklister — signaturen skrives da på alle.
 */
interface HandoverOrderGroup {
  erpOrderNo: string;
  customerName: string | null;
  pLists: HandoverPListDto[];
  allReady: boolean;   // alle plukk ferdige (status >= 50) → kan signeres
  anySigned: boolean;
  signedAt: string | null;
}

const groupByOrder = (rows: HandoverPListDto[]): HandoverOrderGroup[] => {
  const map = new Map<string, HandoverPListDto[]>();
  for (const row of rows) {
    const key = row.erpOrderNo || `PLIST-${row.pListID}`;
    const list = map.get(key) ?? [];
    list.push(row);
    map.set(key, list);
  }
  return Array.from(map.entries()).map(([erpOrderNo, pLists]) => ({
    erpOrderNo,
    customerName: pLists.find(p => p.customerName)?.customerName ?? null,
    pLists,
    allReady: pLists.every(p => p.pListStatus >= 50),
    anySigned: pLists.some(p => p.hasSignature),
    signedAt: pLists.find(p => p.signature1DateTime)?.signature1DateTime ?? null
  }));
};

export const DeliveryHandover: React.FC<DeliveryHandoverProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [groups, setGroups] = useState<HandoverOrderGroup[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signingGroup, setSigningGroup] = useState<HandoverOrderGroup | null>(null);
  // Speil av signingGroup som overlever re-render-racer frem til confirm (T5-funn)
  const signingGroupRef = useRef<HandoverOrderGroup | null>(null);
  const [savingOrder, setSavingOrder] = useState<string | null>(null);
  const [savedOrder, setSavedOrder] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Foto av godset (36334240): bildet lagres som MediaAttachment på ALLE ordrens
  // plukklister (samme nøkkel som signaturen — PList/{pListID}) og blir dermed
  // synlig både under signaturen i Avsluttede plukklister og i mediebiblioteket.
  const photoInputRef = useRef<HTMLInputElement>(null);
  const photoGroupRef = useRef<HandoverOrderGroup | null>(null);
  const [photoSavingOrder, setPhotoSavingOrder] = useState<string | null>(null);
  const [photoSavedOrder, setPhotoSavedOrder] = useState<string | null>(null);
  // Antall bilder tatt denne økten per ordre (kun visuell bekreftelse)
  const [sessionPhotoCounts, setSessionPhotoCounts] = useState<Record<string, number>>({});

  const handleTakePhoto = (group: HandoverOrderGroup) => {
    photoGroupRef.current = group;
    photoInputRef.current?.click();
  };

  const handlePhotoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // tillat samme fil igjen
    const group = photoGroupRef.current;
    if (!file || !group) return;

    setPhotoSavingOrder(group.erpOrderNo);
    setPhotoSavedOrder(null);
    const failedLists: number[] = [];
    try {
      const dataUrl = await compressImage(file);
      const fileName = `utlevering_${group.erpOrderNo}_${Date.now()}.jpg`;
      for (const pList of group.pLists) {
        try {
          await mediaAttachmentService.addAttachment({
            entityType: 'PList',
            entityKey: String(pList.pListID),
            mediaType: 'photo',
            contentType: 'image/jpeg',
            fileName,
            dataUrl,
            description: t('warehouse.modules.deliveryHandover.photoDescription', { '0': group.erpOrderNo },
              `Utlevering ordre ${group.erpOrderNo}`)
          });
        } catch (listErr) {
          console.error(`Handover photo failed for PList ${pList.pListID}:`, listErr);
          failedLists.push(pList.pListID);
        }
      }

      if (failedLists.length > 0) {
        alert(t('warehouse.modules.deliveryHandover.photoSaveFailedLists', { '0': failedLists.join(', ') },
          `Bildet ble ikke lagret på plukkliste ${failedLists.join(', ')}. Prøv igjen.`));
      } else {
        setPhotoSavedOrder(group.erpOrderNo);
        setSessionPhotoCounts(prev => ({ ...prev, [group.erpOrderNo]: (prev[group.erpOrderNo] || 0) + 1 }));
      }
    } catch (err) {
      console.error('Handover photo compression/upload failed:', err);
      alert(t('warehouse.modules.deliveryHandover.photoSaveFailed', {}, 'Kunne ikke lagre bildet. Prøv igjen.'));
    } finally {
      setPhotoSavingOrder(null);
      photoGroupRef.current = null;
    }
  };

  const performSearch = useCallback(async (term: string) => {
    if (term.trim().length < 2) {
      setGroups([]);
      setHasSearched(false);
      return;
    }
    setIsSearching(true);
    setError(null);
    try {
      const rows = await pickingService.searchHandoverPLists(term.trim());
      setGroups(groupByOrder(rows));
      setHasSearched(true);
    } catch (err) {
      console.error('Handover search failed:', err);
      setError(t('warehouse.modules.deliveryHandover.searchError', {}, 'Søket feilet. Prøv igjen.'));
    } finally {
      setIsSearching(false);
    }
  }, [t]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setSavedOrder(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => performSearch(value), 300);
  };

  // Signaturen lagres NØYAKTIG som i plukkbildet (usePickingCompletion.handleSignatureConfirm):
  // hent ferskt hode per plukkliste og send samme feltsett + Signature1 via updatePickingList.
  // Ordrenivå: samme signatur skrives på ALLE ordrens plukklister.
  const handleSignatureConfirm = async (signatureDataUrl: string) => {
    // Les gruppen fra ref (satt ved modal-åpning): state-tilstanden kan i teorien
    // nullstilles av en re-render-race før confirm — en stille retur her ville
    // fått lagringen til å forsvinne uten feilmelding (T5-funn i testrunden).
    const group = signingGroupRef.current ?? signingGroup;
    if (!group) {
      console.error('Handover signature confirm without signing group — nothing saved');
      alert(t('warehouse.modules.deliveryHandover.signSaveFailed', {}, 'Kunne ikke lagre signaturen. Prøv igjen.'));
      return;
    }
    setSavingOrder(group.erpOrderNo);
    const failedLists: number[] = [];
    try {
      for (const pList of group.pLists) {
        try {
          const details = await pickingService.getPickingDetails(pList.pListID);
          const updatedPList = {
            ID: details.header.id,
            CompanyNo: details.header.companyNo,
            PListID: details.header.pListID,
            Description1: details.header.description1,
            Description2: details.header.description2,
            PListDate: details.header.pListDate,
            NumOfOrderLines: details.header.numOfOrderLines,
            Weight: details.header.weight,
            Priority: details.header.priority,
            PListStatus: details.header.pListStatus,
            OperatorID: details.header.operatorID,
            LockedBy: details.header.lockedBy,
            StartWorkDateTime: details.header.startWorkDateTime,
            StopWorkDateTime: details.header.stopWorkDateTime,
            DeliveryDate: details.header.deliveryDate,
            LastUpdate: details.header.lastUpdate,
            Signature1: signatureDataUrl
          };
          await pickingService.updatePickingList(pList.pListID, updatedPList);
        } catch (listErr) {
          console.error(`Handover signature failed for PList ${pList.pListID}:`, listErr);
          failedLists.push(pList.pListID);
        }
      }

      if (failedLists.length > 0) {
        alert(t('warehouse.modules.deliveryHandover.signSaveFailedLists', { '0': failedLists.join(', ') },
          `Signaturen ble ikke lagret på plukkliste ${failedLists.join(', ')}. Prøv igjen.`));
      } else {
        setSavedOrder(group.erpOrderNo);
      }
      setSigningGroup(null);
      signingGroupRef.current = null;
      // Oppdater visningen så signert-status vises med en gang
      await performSearch(searchTerm);
    } finally {
      setSavingOrder(null);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <BackButton onClick={onBack} />
        <PenTool size={22} className="text-teal-600" />
        <h1 className="text-lg font-semibold text-gray-900">
          {t('warehouse.modules.deliveryHandover.title', {}, 'Utlevering')}
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-3xl w-full mx-auto">
        {/* Søk */}
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <ScanTextInput
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('warehouse.modules.deliveryHandover.searchPlaceholder', {}, 'Søk ordrenummer eller kundenavn...')}
            className="w-full pl-10 pr-4 py-3 min-h-[44px] border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
          />
          {isSearching && (
            <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {savedOrder && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
            <CheckCircle size={16} />
            {t('warehouse.modules.deliveryHandover.signSaved', { '0': savedOrder }, `Utlevering signert for ordre ${savedOrder}`)}
          </div>
        )}

        {photoSavedOrder && (
          <div className="bg-teal-50 border border-teal-200 text-teal-800 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
            <Camera size={16} />
            {t('warehouse.modules.deliveryHandover.photoSaved', { '0': photoSavedOrder }, `Bilde lagret på ordre ${photoSavedOrder}`)}
          </div>
        )}

        {/* Resultater gruppert per ordre */}
        {groups.map(group => (
          <div key={group.erpOrderNo} className="bg-white rounded-lg shadow border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-gray-900">{group.erpOrderNo}</div>
                <div className="text-sm text-gray-600 truncate">{group.customerName || '-'}</div>
              </div>
              {/* Foto av godset (36334240) — alltid tilgjengelig, uavhengig av signeringsstatus */}
              <button
                onClick={() => handleTakePhoto(group)}
                disabled={photoSavingOrder === group.erpOrderNo}
                className="flex items-center gap-1.5 px-3 py-2 min-h-[44px] border border-teal-600 text-teal-700 rounded-lg font-medium hover:bg-teal-50 disabled:opacity-50 transition-colors flex-shrink-0"
                title={t('warehouse.modules.deliveryHandover.photoButton', {}, 'Ta bilde av godset')}
              >
                {photoSavingOrder === group.erpOrderNo
                  ? <Loader2 size={16} className="animate-spin" />
                  : <Camera size={16} />}
                <span className="hidden sm:inline">{t('warehouse.modules.deliveryHandover.photoButtonShort', {}, 'Ta bilde')}</span>
                {(sessionPhotoCounts[group.erpOrderNo] || 0) > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-teal-600 text-white text-xs font-bold">
                    {sessionPhotoCounts[group.erpOrderNo]}
                  </span>
                )}
              </button>
              {group.anySigned ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex-shrink-0">
                  <CheckCircle size={12} />
                  {t('warehouse.modules.deliveryHandover.signedBadge', {}, 'Signert')}
                  {group.signedAt && ` ${new Date(group.signedAt).toLocaleString('nb-NO')}`}
                </span>
              ) : group.allReady ? (
                <button
                  onClick={() => { signingGroupRef.current = group; setSigningGroup(group); }}
                  disabled={savingOrder === group.erpOrderNo}
                  className="flex items-center gap-2 px-4 py-2 min-h-[44px] bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors flex-shrink-0"
                >
                  {savingOrder === group.erpOrderNo
                    ? <Loader2 size={16} className="animate-spin" />
                    : <PenTool size={16} />}
                  {t('warehouse.modules.deliveryHandover.signButton', {}, 'Signer utlevering')}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 flex-shrink-0">
                  <AlertCircle size={12} />
                  {t('warehouse.modules.deliveryHandover.notReady', {}, 'Plukk pågår')}
                </span>
              )}
            </div>
            <div className="divide-y divide-gray-50">
              {group.pLists.map(pl => (
                <div key={pl.pListID} className="px-4 py-2 flex items-center gap-3 text-sm">
                  <Package size={14} className="text-gray-400 flex-shrink-0" />
                  <span className="font-mono text-gray-700">{pl.pListID}</span>
                  <StatusBadge value={pl.pListStatus} statusType={1} />
                  <span className="text-gray-500">
                    {t('warehouse.modules.deliveryHandover.linesCount', { '0': `${pl.numOfOrderLines ?? 0}` }, `${pl.numOfOrderLines ?? 0} linjer`)}
                  </span>
                  {pl.hasSignature && <CheckCircle size={14} className="text-green-500 ml-auto flex-shrink-0" />}
                </div>
              ))}
            </div>
          </div>
        ))}

        {hasSearched && !isSearching && groups.length === 0 && !error && (
          <div className="text-center py-10 text-gray-400">
            <Package size={40} className="mx-auto mb-2" />
            <p className="text-sm">{t('warehouse.modules.deliveryHandover.noResults', {}, 'Ingen ordrer funnet — søk på ERP-ordrenummer eller kundenavn')}</p>
          </div>
        )}
      </div>

      {/* Skjult kamera-input for godsbilder (capture=environment åpner kameraet på nettbrett) */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handlePhotoSelected}
      />

      {/* Samme signaturmodal som plukkbildet */}
      <SignatureModal
        isOpen={signingGroup !== null}
        onConfirm={handleSignatureConfirm}
        onClose={() => { signingGroupRef.current = null; setSigningGroup(null); }}
      />
    </div>
  );
};

export default DeliveryHandover;
